import React, { useState } from "react";
import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import { Download, LayoutDashboard, Upload, FolderOpen, GitCompare, Users, LogOut, Pill, FlaskConical, Sliders, ShoppingCart, BarChart3, Menu, X, UserCog, Package, AlertTriangle, Stethoscope, KeyRound, CalendarDays, Sparkles, Gauge, FileText, Inbox, TrendingUp, Boxes, Activity } from "lucide-react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { useLogo, reloadLogo } from "../lib/logo";
import { AyudaIA } from "./AyudaIA";
import { BuscadorGlobal } from "./BuscadorGlobal";
import { VolverAtras } from "./VolverAtras";
import { VideoBienvenida } from "./VideoBienvenida";
import { PieMarca } from "./PieMarca";

const NAV = [
  { to: "/", label: "Cuadro de mando", icon: LayoutDashboard, end: true, testid: "nav-dashboard", mod: "stock" },
  { to: "/ventas/cuadro", label: "Cuadro de ventas", icon: Gauge, testid: "nav-cuadro-ventas", mod: "comercial" },
  { to: "/ventas", label: "Ventas mensuales", icon: BarChart3, end: true, testid: "nav-ventas", mod: "comercial" },
  { section: "Indicadores", tone: "cyan" },
  { to: "/indicadores/stock", label: "Indicadores de stock", icon: Boxes, testid: "nav-indicadores-stock", mod: "stock" },
  { banner: "farmaclass" },
  { section: "Subidas", tone: "amber" },
  { to: "/subidas/abcd", label: "Año + inventario (ABCD)", icon: Upload, testid: "nav-subidas-abcd", mod: "stock" },
  { to: "/subidas/ventas", label: "Ventas mes a mes", icon: CalendarDays, testid: "nav-subidas-ventas", mod: "comercial" },
  { section: "Análisis de stock", tone: "emerald" },
  { to: "/studies", label: "Mis estudios", icon: FolderOpen, testid: "nav-studies", mod: "stock" },
  { to: "/compare", label: "Comparar", icon: GitCompare, testid: "nav-compare", mod: "stock" },
  { to: "/labs", label: "Laboratorios", icon: FlaskConical, testid: "nav-labs", mod: "stock" },
  { to: "/min-max", label: "Mínimos / Máximos", icon: Sliders, testid: "nav-min-max", mod: "stock" },
  { to: "/asistente", label: "Asistente IA", icon: Sparkles, testid: "nav-asistente" },
  { section: "Stock parado", mod: "stock", tone: "red" },
  { to: "/stock-parado/farmacia", label: "Farmacia", icon: Pill, testid: "nav-stock-farmacia", mod: "stock" },
  { to: "/stock-parado/parafarmacia", label: "Parafarmacia", icon: Package, testid: "nav-stock-parafarmacia", mod: "stock" },
  { to: "/stock-parado/caducar", label: "A punto de caducar", icon: AlertTriangle, testid: "nav-stock-caducar", mod: "stock" },
  { section: "Equipo", mod: "comercial", tone: "pink" },
  { to: "/empleados", label: "Empleados", icon: Users, testid: "nav-empleados", mod: "comercial" },
  { to: "/equipo/informes", label: "Informes", icon: FileText, testid: "nav-informes", mod: "comercial" },
  { section: "Consejo", mod: "consejo", tone: "teal" },
  { to: "/consejo", label: "Venta cruzada IA", icon: Stethoscope, end: true, testid: "nav-consejo", mod: "consejo" },
  { section: "Proveedor", mod: "proveedor", tone: "orange" },
  { to: "/reaprovisionamientos", label: "Reaprovisionamientos", icon: ShoppingCart, testid: "nav-reaprovisionamientos", mod: "proveedor" },
  { section: "Recursos", tone: "sky" },
  { to: "/descargas", label: "Descargas y formación", icon: Download, testid: "nav-descargas" },
];

const TONES = {
  sky: { bg: "bg-sky-100", fg: "text-sky-700", solid: "bg-sky-500", title: "text-sky-700", dot: "bg-sky-500" },
  cyan: { bg: "bg-cyan-100", fg: "text-cyan-700", solid: "bg-cyan-500", title: "text-cyan-700", dot: "bg-cyan-500" },
  amber: { bg: "bg-amber-100", fg: "text-amber-700", solid: "bg-amber-500", title: "text-amber-700", dot: "bg-amber-500" },
  emerald: { bg: "bg-emerald-100", fg: "text-emerald-700", solid: "bg-emerald-500", title: "text-emerald-700", dot: "bg-emerald-500" },
  red: { bg: "bg-red-100", fg: "text-red-600", solid: "bg-red-500", title: "text-red-600", dot: "bg-red-500" },
  pink: { bg: "bg-pink-100", fg: "text-pink-700", solid: "bg-pink-500", title: "text-pink-700", dot: "bg-pink-500" },
  teal: { bg: "bg-teal-100", fg: "text-teal-700", solid: "bg-teal-500", title: "text-teal-700", dot: "bg-teal-500" },
  orange: { bg: "bg-orange-100", fg: "text-orange-700", solid: "bg-orange-500", title: "text-orange-700", dot: "bg-orange-500" },
  slate: { bg: "bg-slate-100", fg: "text-slate-600", solid: "bg-slate-600", title: "text-slate-500", dot: "bg-slate-400" },
};

function withTones(items) {
  let tone = "sky";
  return items.map((it) => { if (it.section) tone = it.tone || "slate"; return { ...it, tone: it.tone || tone }; });
}

function NavIcon({ icon: Icon, tone = "slate", active }) {
  const t = TONES[tone];
  return (
    <span className={"h-7 w-7 shrink-0 rounded-lg flex items-center justify-center transition-colors " + (active ? t.solid : `${t.bg} ${t.fg}`)}>
      <Icon size={15} />
    </span>
  );
}

function BannerFarmaClass() {
  return (
    <NavLink to="/farmaclass" data-testid="nav-farmaclass"
             className={({ isActive }) => "group block rounded-xl p-3 bg-[#06281f] text-white hover:bg-[#0a3a2d] transition-colors " + (isActive ? "ring-2 ring-emerald-400" : "")}>
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-emerald-300"><TrendingUp size={12} /> FarmaClass</div>
      <div className="text-sm font-bold mt-1 leading-snug">¿Quieres aumentar tu rentabilidad?</div>
      <div className="text-[11px] text-emerald-100/80 mt-0.5 group-hover:text-white">Descubre cómo ganar más con genéricos →</div>
    </NavLink>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const logo = useLogo();
  React.useEffect(() => { if (user?.id) reloadLogo(); }, [user?.id]);
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const modules = isAdmin ? null : (user?.modules || null);
  const lic = user?.license;
  const navItems = NAV.filter((item) => !modules || !item.mod || modules.includes(item.mod));

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  React.useEffect(() => { setOpen(false); }, [location.pathname]);
  const [nuevos, setNuevos] = useState(0);
  React.useEffect(() => {
    if (!user?.id || isAdmin) return undefined;
    const cargar = () => api.get("/descargas/novedades").then((r) => setNuevos(r.data.nuevos)).catch(() => {});
    const limpiar = () => setNuevos(0);
    cargar();
    window.addEventListener("descargas-vistas", limpiar);
    return () => window.removeEventListener("descargas-vistas", limpiar);
  }, [user?.id, isAdmin]);

  if (lic && lic.effective_status && lic.effective_status !== "activa") {
    const sinLicencia = lic.effective_status === "sin licencia";
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="license-blocked">
        <div className="card p-8 max-w-lg text-center">
          <div className="h-12 w-12 rounded-full bg-red-100 text-red-700 flex items-center justify-center mx-auto mb-4">
            <KeyRound size={22} />
          </div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight mb-2">
            {sinLicencia ? "Sin licencia activa" : `Licencia ${lic.effective_status}`}
          </h1>
          <p className="text-slate-600 mb-1">
            {sinLicencia ? (lic.pharmacy_name || "") : <>Licencia <span className="font-mono">{lic.code}</span> · {lic.pharmacy_name}</>}
          </p>
          <p className="text-slate-600 mb-6">
            {lic.effective_status === "caducada"
              ? `Caducó el ${lic.end_date ? new Date(lic.end_date).toLocaleDateString("es-ES") : "—"}. Renuévala para seguir utilizando la plataforma.`
              : "Contacta con administración para activar el acceso."}
          </p>
          <div className="flex gap-2 justify-center">
            <a className="btn btn-primary" href="mailto:soporte@farmanaliza.com?subject=Renovación%20de%20licencia" data-testid="renew-contact-button">Contactar para renovar</a>
            <button className="btn btn-outline" onClick={onLogout} data-testid="blocked-logout-button">Cerrar sesión</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen lg:flex bg-slate-50" data-testid="app-layout">
      <div className="lg:hidden sticky top-0 z-[60] flex items-center gap-3 bg-white border-b border-slate-200 px-4 py-3">
        <button onClick={() => setOpen((o) => !o)} className="h-9 w-9 rounded-lg border border-slate-200 flex items-center justify-center"
                aria-label="Menú" data-testid="mobile-menu-toggle">
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
        <div className="font-display font-extrabold tracking-tight flex items-center gap-2">
          {logo.url && <img src={logo.url} alt="" className="h-7 max-w-[90px] object-contain" data-testid="mobile-logo" />}
          <img src="/img/farmanaliza-logo.png?v=3" alt="Farmanaliza" className="h-10 w-auto" data-testid="mobile-brand" />
        </div>
      </div>

      <aside className={"w-64 shrink-0 bg-white border-r border-slate-200 flex-col " +
        (open ? "flex fixed inset-y-0 left-0 z-50 pt-14 lg:pt-0 overflow-y-auto shadow-xl " : "hidden ") + "lg:flex"} data-testid="sidebar">
        <div className="px-5 py-5 border-b border-slate-200">
          <div className="flex flex-col items-start gap-2">
            <img src="/img/farmanaliza-logo.png?v=3" alt="Farmanaliza · Stocks, ventas, equipo" className="w-full max-w-[200px] h-auto" data-testid="sidebar-brand" />
            {logo.url && <img src={logo.url} alt="Logo de la farmacia" className="h-8 max-w-[120px] object-contain" data-testid="sidebar-logo" />}
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {withTones(navItems).map((item, i) => item.banner ? <div key="farmaclass" className="pt-2"><BannerFarmaClass /></div> : item.section ? (
            <div key={`s-${i}`}>
              <div className="my-2 mx-3 border-t border-slate-200" />
              <div className={"px-3 mb-1 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 " + TONES[item.tone].title}>
                <span className={"h-1.5 w-1.5 rounded-full " + TONES[item.tone].dot} />{item.section}
              </div>
            </div>
          ) : (
            <NavLink key={item.to} to={item.to} end={item.end}
                     className={({ isActive }) => "sidebar-item " + (isActive ? "active" : "")} data-testid={item.testid}>
              {({ isActive }) => (<><NavIcon icon={item.icon} tone={item.tone} active={isActive} />{item.label}
                {item.to === "/descargas" && nuevos > 0 && <span className="ml-auto text-[10px] font-extrabold bg-red-500 text-white rounded-full px-1.5 py-0.5 animate-pulse" data-testid="nav-descargas-nuevos">{nuevos} nuevo{nuevos > 1 ? "s" : ""}</span>}</>)}
            </NavLink>
          ))}
          {isAdmin && (
            <>
              <div className="my-2 mx-3 border-t border-slate-200" />
              <NavLink to="/admin/solicitudes" className={({ isActive }) => "sidebar-item " + (isActive ? "active" : "")} data-testid="nav-admin-solicitudes">
                <NavIcon icon={Inbox} />
                Solicitudes web
              </NavLink>
              <NavLink to="/admin/licencias" className={({ isActive }) => "sidebar-item " + (isActive ? "active" : "")} data-testid="nav-admin-licencias">
                <NavIcon icon={KeyRound} />
                Licencias (admin)
              </NavLink>
              <NavLink to="/admin/users" className={({ isActive }) => "sidebar-item " + (isActive ? "active" : "")} data-testid="nav-admin-users">
                <NavIcon icon={Users} />
                Usuarios (admin)
              </NavLink>
            </>
          )}
        </nav>

        <div className="border-t border-slate-200 p-3">
          <div className="px-3 py-2">
            <div className="text-xs font-semibold text-slate-900 truncate" data-testid="user-name">{user?.name}</div>
            <div className="text-[11px] text-slate-500 truncate">{user?.email}</div>
            {user?.pharmacy_name && <div className="text-[11px] text-slate-500 truncate">{user.pharmacy_name}</div>}
            <div className="mt-1">
              <span className={"text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded " + (isAdmin ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700")}>
                {user?.role}
              </span>
            </div>
          </div>
          <NavLink to="/perfil" className={({ isActive }) => "sidebar-item " + (isActive ? "active" : "")} data-testid="nav-perfil">
            <NavIcon icon={UserCog} /> Mi perfil
          </NavLink>
          <button data-testid="logout-button" onClick={onLogout} className="sidebar-item w-full">
            <NavIcon icon={LogOut} /> Cerrar sesión
          </button>
        </div>
      </aside>

      {open && <div className="lg:hidden fixed inset-0 z-40 bg-slate-900/40" onClick={() => setOpen(false)} />}

      <main className="flex-1 min-w-0 overflow-x-hidden lg:overflow-x-clip lg:overflow-y-visible">
        <div className="relative lg:sticky lg:top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200 px-4 sm:px-8 py-2.5 flex items-center gap-3" data-testid="barra-superior">
          <VolverAtras />
          <div className="flex-1 min-w-0"><BuscadorGlobal /></div>
        </div>
        {modules && (() => {
          const p = location.pathname;
          const needed = p.startsWith("/ventas") ? "comercial"
            : p.startsWith("/consejo") ? "consejo"
              : p.startsWith("/reaprovisionamientos") || p.startsWith("/suppliers") ? "proveedor"
                : null;
          return needed && !modules.includes(needed);
        })() ? (
          <div className="p-8" data-testid="module-not-licensed">
            <div className="card p-8 max-w-lg">
              <h1 className="font-display text-2xl font-extrabold tracking-tight mb-2">Módulo no incluido</h1>
              <p className="text-slate-600">Tu licencia no incluye este apartado. Contacta con administración para ampliarla.</p>
            </div>
          </div>
        ) : <Outlet />}
        <PieMarca />
        <VideoBienvenida />
      </main>
      <AyudaIA />
    </div>
  );
}
